import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { useAppSelector } from 'src/store/hooks';
import { selectProgram } from 'src/store/slices/programSlice';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { useSearchParams } from 'react-router-dom';
import Heading from '@amzn/meridian/heading';
import { escapeRegExp } from 'lodash';
import Text from '@amzn/meridian/text';
import useGetMacroplanogramResults from 'src/store/hooks/useGetMacroplanogramResults';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import usePublishZone from 'src/store/hooks/usePublishResolver';
import usePutZoneMapConfig from 'src/store/hooks/usePutZoneMapConfig';
import { Zone } from 'src/graphql/generated/graphql';
import useGetFloorplan from '@store/hooks/useGetFloorplan';
import ZoneCreationComponent from '@components/agile/agileComponents/ZoneCreationComponent';
import Divider from '@amzn/meridian/divider';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import Modal from '@amzn/meridian/modal';
import useGetCategory from 'src/store/hooks/useGetCategory';
import Loader from '@amzn/meridian/loader';
import Toaster from '@amzn/meridian/toaster';
import { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import Toggle from '@amzn/meridian/toggle';
import {
  createMissingFixtureDataToast,
  createNoFixtureToast,
  createFixtureAlreadyAssignedToast,
  createBulkSelectionSuccessToast,
  createBulkSelectionFilteredToast,
  createEmptySelectionToast,
} from '@components/agile/utils/ToasterUtils';
import ViewZoneComponent from './zoneViewer/ViewZoneComponent';
import ZoneViewerModalComponent from './zoneViewer/ZoneViewerModalComponent';
import { SwitchButtonValue, ZoneCreationProperties } from './types/zoneMapType';
import buildFixtureIdToZoneDataMap, {
  buildFixtureIdToEditingZoneDataMap,
  filterZoneCreationProperties,
  generateZoneNameKeyMap,
  transformFPData,
  validateEditingZoneData,
  validatePublishZoneLiveDate,
} from './zoneViewer/zoneMapDataHelper';
import { ProductGroupOption } from './types/ProductTaxonomyTypes';
import StoreOrModeChangeConfirmationModal from './zoneViewer/StoreOrModeChangeConfirmationModal';
import filterZoneData from './zoneViewer/copyToNewHelper';
import ZoneCreationMenu from './zoneViewer/zoneCreationMenu';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import useGetProductGroups from 'src/store/hooks/useGetProductGroups';
import { useFixtureSelection } from './hooks/useFixtureSelection';

export interface ZoneViewerProps {
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (value: AgileURLParameterTypes) => void;
  switchButtonValue: string | undefined;
  setSwitchButtonValue: (value: SwitchButtonValue | undefined) => void;
}

const ZoneViewer = (props: ZoneViewerProps) => {
  const { urlParams, setUrlParameter, switchButtonValue, setSwitchButtonValue } = props;
  const programType = useAppSelector(selectProgram);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [newSwitchButtonValue, setNewSwitchButtonValue] = useState<SwitchButtonValue | undefined>(undefined);
  const [isLoadingPublishZone, setIsLoadingPublishZone] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [store, setStore] = useState(urlParams.storeId);
  const [date, setDate] = useState(urlParams.date || new Date().toISOString().split('T')[0]);
  const [selectedZoneId] = useState(urlParams.zoneId);
  const [publishZoneSuccessful, setPublishZoneSuccessful] = useState(false);
  const [productGroupFilter, setProductGroupFilter] = useState('');
  const [selectedProductGroup, setSelectedProductGroup] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [publishZoneErrorMsg, setPublishZoneErrorMsg] = useState<string | undefined>(undefined);
  const [selectedFixtures, setSelectedFixtures] = useState<string[]>([]);
  const [showDraft, setShowDraft] = useState(false);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [toasts, setToasts] = useState<ToasterToasts[]>([]);
  const [activeZoneIdx, setActiveZoneIdx] = useState<number>(0);
  const onCloseToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const openNoFixtureToaster = useCallback(() => setToasts((prev) => prev.concat(createNoFixtureToast())), []);
  const openMissingDataToaster = useCallback((fixtureCount: number) => {
    setToasts((prev) => prev.concat(createMissingFixtureDataToast(fixtureCount)));
  }, []);

  // Auto-dismiss toasts after their dismissAfter time
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    toasts.forEach((toast) => {
      const dismissAfter = (toast as { dismissAfter?: number }).dismissAfter;
      if (dismissAfter) {
        const timer = setTimeout(() => {
          onCloseToast(toast.id);
        }, dismissAfter);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [toasts, onCloseToast]);
  const timeStamp = new Date(date).getTime();
  // Added a flag to display draft related changes
  // TODO: remove the flag once all the changes for draft are deployed
  const draftChanges = true;
  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);
  const { mutateAsync: publishZone } = usePublishZone();
  const { mutateAsync: putZoneMapConfig } = usePutZoneMapConfig();
  const [copyToNewDate, setCopyToNewDate] = useState(urlParams.date);
  const copyToNewTimestamp = new Date(copyToNewDate).getTime();
  const [newStore, setNewStore] = useState<string | undefined>(undefined);
  const [shouldFetchFuture, setShouldFetchFuture] = useState(false);
  const [storeOrModeChangeConfirmationModal, setStoreOrModeChangeConfirmationModal] = useState<{
    open: boolean;
    showStoreWarning: boolean;
  }>({ open: false, showStoreWarning: false });
  const { zoneNameList, isSuccess: isGetZoneDataSuccess } = useGetMacroplanogramResults({
    storeId: store,
    timestamp: timeStamp,
    includeFuture: shouldFetchFuture && showDraft,
    includeDraft: true,
  });
  const {
    floorplanList,
    isLoading: isGetFloorplanLoading,
    isSuccess: isGetFloorplanSuccess,
  } = useGetFloorplan({
    storeId: store,
    date: switchButtonValue !== SwitchButtonValue.CopyToNewMap ? timeStamp : copyToNewTimestamp,
  });
  const fixtures = floorplanList.length > 0 ? (floorplanList[0].fixtures ?? []) : [];
  const fpData = transformFPData(fixtures);
  // agileFixtures include fixtures without length, width, height, etc
  const agileFixtures = fixtures.filter((fixture) => fixture.isAgile);

  const filteredZoneNameListDraft = useMemo(() => {
    if (!showDraft) {
      return zoneNameList.filter((zone) => !zone.isDraft);
    }
    return zoneNameList.filter((zone) => zone.isDraft);
  }, [zoneNameList, showDraft]);
  const { productGroupList } = useGetProductGroups();

  useEffect(() => {
    setShowDraft(false);
  }, [switchButtonValue]);
  // Handle the future draft plan fetch logic for view zone map
  useEffect(() => {
    if (showDraft && zoneNameList.length > 0) {
      const draftZones = zoneNameList.filter((zone) => zone.isDraft);
      if (draftZones.length === 0) {
        setShouldFetchFuture(true);
      } else {
        setShouldFetchFuture(false);
      }
    } else {
      setShouldFetchFuture(false);
    }
  }, [zoneNameList, showDraft]);

  // Handle date update when future drafts are fetched for view zone map
  useEffect(() => {
    if (showDraft && shouldFetchFuture && zoneNameList.length > 0) {
      const futureDraftZones = zoneNameList.filter((zone) => zone.isDraft);
      if (futureDraftZones.length > 0) {
        const nearestTimestamp = Math.min(...futureDraftZones.map((zone) => parseInt(zone.startTime, 10)));
        setDate(new Date(nearestTimestamp).toISOString().split('T')[0]);
      }
    }
  }, [zoneNameList, showDraft, shouldFetchFuture]);

  // generate zoneNameKeyMap for AMS zone data
  const zoneNameKeyMapForView = generateZoneNameKeyMap(filteredZoneNameListDraft);

  const fixtureIdToZoneDataMapForView = useMemo(
    () => buildFixtureIdToZoneDataMap(filteredZoneNameListDraft, fpData, zoneNameKeyMapForView),
    [filteredZoneNameListDraft, fpData, zoneNameKeyMapForView],
  );

  useEffect(() => {
    if (
      (date !== '' &&
        store !== '' &&
        switchButtonValue !== undefined &&
        switchButtonValue !== SwitchButtonValue.CopyToNewMap) ||
      (copyToNewDate !== '' && store !== '' && switchButtonValue === SwitchButtonValue.CopyToNewMap)
    ) {
      if (!isGetFloorplanLoading && (fixtures.length === 0 || agileFixtures.length === 0)) {
        openNoFixtureToaster();
        console.warn('No fixtures found.');
      }
      const fixturesMissingAttributes = fixtures.filter(
        (fixture) =>
          fixture.height == null ||
          fixture.width == null ||
          fixture.depth == null ||
          fixture.rotation == null ||
          fixture.translation == null,
      );
      if (fixturesMissingAttributes.length > 0) {
        openMissingDataToaster(fixturesMissingAttributes.length);
        console.warn(
          `Found ${fixturesMissingAttributes.length} fixtures missing dimensions or pos data.`,
          fixturesMissingAttributes,
        );
      }
    }
  }, [floorplanList, switchButtonValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialZoneCreationPropertiesMap: Map<number, ZoneCreationProperties> = new Map();
  initialZoneCreationPropertiesMap.set(0, {
    zoneIdx: 0,
    zoneName: '',
    productGroup: '',
    categoryId: '',
    subcategoryIds: [],
    subcategoryNames: [],
    fixtureIds: [],
    fixtureModels: new Map(),
    facings: 0,
  });

  const [zoneCreationProperties, setZoneCreationProperties] = useState<Map<number, ZoneCreationProperties>>(
    initialZoneCreationPropertiesMap,
  );
  const [zoneCreationValidationErrors, setZoneCreationValidationErrors] = useState<string[]>([]);

  // Integrate fixture selection hook for visual selection
  const isSelectionEnabled =
    switchButtonValue === SwitchButtonValue.CreateZoneMap || switchButtonValue === SwitchButtonValue.CopyToNewMap;

  const fixtureSelection = useFixtureSelection({
    zoneCreationProperties,
    setZoneCreationProperties,
    selectedFixtures,
    setSelectedFixtures,
    agileFixtures,
    activeZoneIdx,
  });

  // Wrapper functions to handle selection results and show toasts
  const handleFixtureClickWithFeedback = useCallback(
    (fixtureId: string, modifierKeys: { shift?: boolean; ctrl?: boolean; meta?: boolean }) => {
      const normalizedModifierKeys = {
        shift: modifierKeys.shift ?? false,
        ctrl: modifierKeys.ctrl ?? false,
        meta: modifierKeys.meta ?? false,
      };
      const result = fixtureSelection.handleFixtureClick(fixtureId, normalizedModifierKeys);
      if (result && !result.success && result.error) {
        const assignedZone = fixtureSelection.getFixtureAssignedZone(fixtureId);
        if (assignedZone) {
          setToasts((prev) => [...prev, createFixtureAlreadyAssignedToast(fixtureId, assignedZone)]);
        }
      }
    },
    [fixtureSelection, setToasts],
  );

  const handleRectangleSelectionWithFeedback = useCallback(
    (fixtureIds: string[]) => {
      const result = fixtureSelection.handleRectangleSelection(fixtureIds);
      if (result.filteredCount > 0) {
        setToasts((prev) => [
          ...prev,
          createBulkSelectionFilteredToast(result.selectedCount + result.filteredCount, result.filteredCount),
        ]);
      }
    },
    [fixtureSelection, setToasts],
  );

  const handleCommitSelectionWithFeedback = useCallback(() => {
    const result = fixtureSelection.commitSelection();
    const currentZone = zoneCreationProperties.get(activeZoneIdx);
    const zoneName = currentZone?.zoneName || `Zone ${activeZoneIdx + 1}`;

    if (result.success && result.selectedCount > 0) {
      setToasts((prev) => [...prev, createBulkSelectionSuccessToast(result.selectedCount, zoneName)]);
    } else if (result.error) {
      setToasts((prev) => [...prev, createEmptySelectionToast()]);
    }
  }, [fixtureSelection, zoneCreationProperties, activeZoneIdx, setToasts]);

  useEffect(() => {
    if (switchButtonValue === SwitchButtonValue.CreateZoneMap && store && date && isGetFloorplanSuccess) {
      const filteredZoneCreationProperties = filterZoneCreationProperties(zoneCreationProperties, agileFixtures);
      const selectedFixturesAfterFiltering = Array.from(filteredZoneCreationProperties.values()).flatMap(
        (zone) => zone.fixtureIds,
      );
      setSelectedFixtures(selectedFixturesAfterFiltering);
      setZoneCreationProperties(filteredZoneCreationProperties);
    }
  }, [date, isGetFloorplanSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoneCreationPropertiesValues = Array.from(zoneCreationProperties.values());
  // generate zoneNameKeyMap for zone data from side bar
  const zoneNameKeyMapForEditingZone = generateZoneNameKeyMap(zoneCreationPropertiesValues);
  const fixtureIdToEditingZoneDataMap = useMemo(
    () => buildFixtureIdToEditingZoneDataMap(zoneCreationProperties, fpData, zoneNameKeyMapForEditingZone),
    [zoneCreationProperties, fpData, zoneNameKeyMapForEditingZone],
  );

  if (programType == null) {
    throw new Error('ProgramType is not initialized');
  }

  const updateProductGroup = (zoneIdx: number, productGroup?: string) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        productGroup: productGroup || '',
      });
      return newMap;
    });
  };

  const updateCategoryId = (zoneIdx: number, categoryId?: string) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        categoryId: categoryId || '',
      });
      return newMap;
    });
  };

  const updateSubcategories = (zoneIdx: number, subcategoryIds?: string[], subcategoryNames?: string[]) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        subcategoryIds: subcategoryIds || [],
        subcategoryNames: subcategoryNames || [],
        zoneName: subcategoryNames?.sort().join('/') ?? '',
      });
      return newMap;
    });
  };

  const updateFixtureIds = (zoneIdx: number, fixtureIds?: string[]) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      const currentZone = prev.get(zoneIdx)!;

      // Create fixture models mapping only if nextGen is enabled
      const fixtureModels = nextGenEnabled ? new Map(currentZone.fixtureModels || new Map()) : new Map();

      if (nextGenEnabled && fixtureIds) {
        // Add models for new fixtures
        fixtureIds.forEach((fixtureId) => {
          if (!fixtureModels.has(fixtureId)) {
            const fixture = agileFixtures.find((f) => f.id === fixtureId);
            if (fixture?.model) {
              fixtureModels.set(fixtureId, fixture.model);
            }
          }
        });

        // Remove models for fixtures that are no longer selected
        const currentFixtureIds = currentZone.fixtureIds || [];
        currentFixtureIds.forEach((fixtureId) => {
          if (!fixtureIds.includes(fixtureId)) {
            fixtureModels.delete(fixtureId);
          }
        });
      } else {
        // Clear all fixture models if nextGen is disabled or no fixtures selected
        fixtureModels.clear();
      }

      newMap.set(zoneIdx, {
        ...currentZone,
        fixtureIds: fixtureIds || [],
        fixtureModels,
      });
      return newMap;
    });
    setSelectedFixtures((prev) => {
      const newSelectedFixtures = prev.filter(
        (fixture) => !zoneCreationProperties.get(zoneIdx)?.fixtureIds.includes(fixture),
      );
      if (fixtureIds) {
        newSelectedFixtures.push(...fixtureIds);
      }
      return newSelectedFixtures;
    });
  };

  const updateFacings = (zoneIdx: number, facings: number) => {
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...prev.get(zoneIdx)!,
        facings,
      });
      return newMap;
    });
  };

  const addAnotherZone = (zoneIdx: number) => {
    setZoneCreationProperties((prevState) => {
      const newMap = new Map(prevState);
      newMap.set(zoneIdx, {
        zoneIdx,
        zoneName: '',
        productGroup: selectedProductGroup,
        categoryId: selectedCategory,
        subcategoryIds: [],
        subcategoryNames: [],
        fixtureIds: [],
        fixtureModels: new Map(),
        facings: 0,
      });
      return newMap;
    });
    // Set the newly created zone as active
    setActiveZoneIdx(zoneIdx);
  };

  const removeZone = (zoneIdx: number) => {
    if (zoneCreationProperties.size > 1) {
      setZoneCreationProperties((prevState) => {
        const newMap = new Map(prevState);
        setSelectedFixtures((prev) => {
          const newSelectedFixtures = prev.filter((fixture) => !newMap.get(zoneIdx)?.fixtureIds.includes(fixture));
          return newSelectedFixtures;
        });
        newMap.delete(zoneIdx);
        return newMap;
      });

      // If the removed zone was active, set the first remaining zone as active
      if (activeZoneIdx === zoneIdx) {
        const remainingZones = Array.from(zoneCreationProperties.keys()).filter((idx) => idx !== zoneIdx);
        if (remainingZones.length > 0) {
          setActiveZoneIdx(remainingZones[0]);
        }
      }
    } else {
      setZoneCreationValidationErrors(['Cannot remove zone. The zone map must have at least one zone.']);
    }
  };

  const validateZoneMap = (): Set<string> => {
    const publishZoneDateError = validatePublishZoneLiveDate(switchButtonValue, copyToNewDate);
    const isWholeFoods = programType === 'WholeFoods';
    const zoneDataErrors = validateEditingZoneData(zoneCreationProperties, agileFixtures, isWholeFoods);
    const errors: Set<string> = new Set([...publishZoneDateError, ...zoneDataErrors]);
    setZoneCreationValidationErrors([...errors]);
    return errors;
  };

  const handleDraftZoneCreationSubmit = () => {
    // Including this log statement to help with debugging and integration of components
    // TODO: remove this line after integrating confirmation modal and publish zone map resolver
    console.log('zoneMap: ', zoneCreationProperties);

    setSaveAsDraft(true);
    // Open confirmation modal if validations pass
    if (validateZoneMap().size === 0) {
      setOpenConfirmationModal(true);
    }
  };

  const zonesToUpdate: Zone[] = Array.from(zoneCreationProperties.values()).map((value) => ({
    name: value.zoneName,
    subcategoryIds: value.subcategoryIds,
    fixtureIds: value.fixtureIds,
    facings: value.facings,
  }));

  const onPublishButton = useCallback(async () => {
    setIsLoadingPublishZone(true);

    try {
      let zoneMapConfigId = null;

      // If saving as draft, save zone config FIRST
      if (saveAsDraft && nextGenEnabled) {
        // Call putZoneMapConfig with all zones in a single call
        const zones = Array.from(zoneCreationProperties.values()).map((zone, index) => ({
          name: zone.zoneName || `Zone-${index + 1}`,
          fixtures: zone.fixtureIds.map((fixtureId) => ({
            id: fixtureId,
            fixtureType: zone.fixtureModels?.get(fixtureId) || 'DEFAULT',
            facings: zone.facings || 0,
          })),
        }));

        const putZoneMapConfigResponse = await putZoneMapConfig({
          input: {
            storeId: store,
            productGroup: selectedProductGroup,
            zoneMapConfigName: store, // Use store ID as zone map config name
            zones,
          },
        });

        zoneMapConfigId = putZoneMapConfigResponse.putZoneMapConfig.zoneMapConfigId;
      }

      // Then publish zone
      const resp = await publishZone({
        input: {
          storeId: store,
          zoneMap: zonesToUpdate,
          timestamp: switchButtonValue === SwitchButtonValue.CopyToNewMap ? copyToNewTimestamp : timeStamp,
          isDraft: saveAsDraft,
          zoneMapConfigId: zoneMapConfigId,
        },
      });

      if (resp.publishZone.error === null) {
        setPublishZoneSuccessful(true);
        // Reset/clear zone creation properties on successful publish
        setZoneCreationProperties(initialZoneCreationPropertiesMap);
      } else {
        setPublishZoneErrorMsg(resp.publishZone.error?.errorMessage);
      }
    } catch (error) {
      setPublishZoneErrorMsg(error instanceof Error ? error.message : 'Operation failed');
    } finally {
      setOpenConfirmationModal(false);
      setIsLoadingPublishZone(false);
    }
  }, [
    publishZone,
    putZoneMapConfig,
    selectedProductGroup,
    zoneCreationProperties,
    store,
    zonesToUpdate,
    timeStamp,
    copyToNewTimestamp,
    initialZoneCreationPropertiesMap,
    switchButtonValue,
    saveAsDraft,
    nextGenEnabled,
  ]);

  const handleProductGroupChange = (productGroup: string) => {
    setSelectedProductGroup(productGroup);
    zoneCreationProperties.forEach((_, zoneIdx) => {
      updateProductGroup(zoneIdx, productGroup);
      updateCategoryId(zoneIdx, undefined);
      updateSubcategories(zoneIdx, [], []);
    });
    setSelectedCategory('');
  };
  const productGroupFilterRegExp = new RegExp(escapeRegExp(productGroupFilter), 'i');
  const programProductGroupOptions: ProductGroupOption[] = productGroupList.map((group) => ({
    label: group.name,
    value: group.id,
  }));
  const filteredProductGroupOptions = programProductGroupOptions.filter(
    (option: ProductGroupOption) => !productGroupFilter || productGroupFilterRegExp.test(option.label),
  );

  const productTaxonomy = useGetCategory({
    productGroup: selectedProductGroup,
  });
  const categoryFilterRegExp = new RegExp(escapeRegExp(categoryFilter), 'i');
  const filteredCategoryOptions = productTaxonomy?.categoryList?.filter(
    (option) => !categoryFilter || categoryFilterRegExp.test(option.categoryName),
  );
  const categoryOnChange = (category: string): void => {
    setSelectedCategory(category);
    zoneCreationProperties.forEach((_, zoneIdx) => {
      updateCategoryId(zoneIdx, category);
      updateSubcategories(zoneIdx, [], []);
    });
  };

  const onClosingSuccessfulModal = () => {
    if (switchButtonValue === SwitchButtonValue.CopyToNewMap && copyToNewDate) {
      setDate(copyToNewDate);
    }
    setSwitchButtonValue(SwitchButtonValue.ViewZoneMap);
  };

  const updateZoneCreationPropertiesForCopyToNew = () => {
    const initialZoneCreationPropertiesCopyToNew: Map<number, ZoneCreationProperties> = new Map();
    initialZoneCreationPropertiesCopyToNew.set(0, {
      zoneIdx: 0,
      zoneName: '',
      productGroup: '',
      categoryId: '',
      subcategoryIds: [],
      subcategoryNames: [],
      fixtureIds: [],
      fixtureModels: new Map(),
      facings: 0,
    });
    const initialSelectedfixturesForCopyToNew: string[] = [];
    if (isGetZoneDataSuccess) {
      // Filter out draft zones before processing
      const nonDraftZones = zoneNameList.filter((zone) => !zone.isDraft);
      const filteredZoneNameList = filterZoneData(nonDraftZones, agileFixtures);
      filteredZoneNameList.forEach((zone, zoneIdx) => {
        // Create fixture models mapping only if nextGen is enabled and floorplan data is available
        const fixtureModels = new Map<string, string>();
        if (nextGenEnabled && isGetFloorplanSuccess) {
          zone.unParsedFixtureIds.forEach((fixtureId) => {
            const fixture = agileFixtures.find((f) => f.id === fixtureId);
            if (fixture?.model) {
              fixtureModels.set(fixtureId, fixture.model);
            }
          });
        }

        initialZoneCreationPropertiesCopyToNew.set(zoneIdx, {
          zoneIdx,
          zoneName: zone.zoneName,
          productGroup: selectedProductGroup,
          categoryId: selectedCategory,
          subcategoryIds: zone.subcategoryIds,
          subcategoryNames: zone.subcategoryNames,
          fixtureIds: zone.unParsedFixtureIds,
          fixtureModels,
          facings: 0,
        });
        initialSelectedfixturesForCopyToNew.push(...zone.unParsedFixtureIds);
      });
    }
    setSelectedFixtures(initialSelectedfixturesForCopyToNew);
    setZoneCreationProperties(initialZoneCreationPropertiesCopyToNew);
  };

  const onClickCopyToNew = () => {
    setCopyToNewDate(date);
    updateZoneCreationPropertiesForCopyToNew();
  };

  const handleSwitchButtonChange = (newButtonValue: SwitchButtonValue | undefined) => {
    if (newButtonValue === SwitchButtonValue.CopyToNewMap) {
      onClickCopyToNew();
    } else if (newButtonValue === SwitchButtonValue.CreateZoneMap) {
      setZoneCreationProperties(initialZoneCreationPropertiesMap);
      setSelectedFixtures([]);
    }
    setSwitchButtonValue(newButtonValue);
  };

  const handleButtonGroupChange = (newButtonValue: SwitchButtonValue | undefined) => {
    // Check if the button value is changing
    if (
      (switchButtonValue === SwitchButtonValue.CopyToNewMap || switchButtonValue === SwitchButtonValue.CreateZoneMap) &&
      newButtonValue !== switchButtonValue &&
      date &&
      store
    ) {
      setNewSwitchButtonValue(newButtonValue);
      setStoreOrModeChangeConfirmationModal({
        open: true,
        showStoreWarning: false,
      });
    } else if (switchButtonValue === SwitchButtonValue.ViewZoneMap) {
      handleSwitchButtonChange(newButtonValue);
    } else {
      if (newButtonValue === SwitchButtonValue.CopyToNewMap) {
        onClickCopyToNew();
      }
      setSwitchButtonValue(newButtonValue);
    }
    setShowDraft(false);
  };

  const onConfirmSwitchButton = () => {
    handleSwitchButtonChange(newSwitchButtonValue);
    setStoreOrModeChangeConfirmationModal({
      open: false,
      showStoreWarning: false,
    });
  };

  const onClickStoreSelect = (newStoreValue: string) => {
    if (
      (switchButtonValue === SwitchButtonValue.CopyToNewMap || switchButtonValue === SwitchButtonValue.CreateZoneMap) &&
      store &&
      newStoreValue !== store
    ) {
      setNewStore(newStoreValue);
      setStoreOrModeChangeConfirmationModal({
        open: true,
        showStoreWarning: true,
      });
    } else {
      setStore(newStoreValue);
    }
  };

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store,
          date,
        },
        urlParams.demo,
      ),
    );
    setUrlParameter({
      storeId: store,
      date,
      zoneId: selectedZoneId,
      demo: urlParams.demo,
    });
  }, [date, store, setSearchParams, setUrlParameter, selectedZoneId, urlParams.demo]);

  useEffect(() => {
    if (
      isGetZoneDataSuccess &&
      isGetFloorplanSuccess &&
      switchButtonValue === SwitchButtonValue.CopyToNewMap &&
      !storeOrModeChangeConfirmationModal.open
    ) {
      updateZoneCreationPropertiesForCopyToNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGetZoneDataSuccess,
    switchButtonValue,
    selectedProductGroup,
    selectedCategory,
    store,
    date,
    copyToNewDate,
    isGetFloorplanSuccess,
  ]);

  // Initialize selectedProductGroup based on program type
  useEffect(() => {
    if (productGroupList.length > 0 && !selectedProductGroup) {
      // Try to find "Produce" first, otherwise use the first item
      const produceGroup = productGroupList.find((pg) => pg.name === 'Produce');
      setSelectedProductGroup(produceGroup ? produceGroup.name : productGroupList[0].name);
    }
  }, [productGroupList, selectedProductGroup]);

  useEffect(() => {
    if (filteredCategoryOptions?.length > 0 && selectedProductGroup) {
      const ambientCategoryOption = filteredCategoryOptions.find(
        (option) => option.categoryName.toLowerCase() === 'ambient',
      );
      if (ambientCategoryOption) {
        setSelectedCategory(ambientCategoryOption.categoryId);
      }
    } else {
      setSelectedCategory('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategoryOptions]);

  return (
    <>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast) => <Alert toast {...toast} />}
      </Toaster>
      <Column width="100%" height="100%">
        <ZoneCreationMenu
          switchButtonValue={switchButtonValue}
          onClickStoreSelect={onClickStoreSelect}
          handleButtonGroupChange={handleButtonGroupChange}
          store={store}
          date={date}
          setDate={setDate}
        />
        {!(store && date && switchButtonValue !== undefined) && (
          <Column alignmentHorizontal="center" alignmentVertical="center" height="100%" heights={['10%', '10%', '0%']}>
            <Heading level={1} type="h600">
              Zone Creation Dashboard
            </Heading>
            <Heading level={1} type="h200">
              Please select a store, date, and button to get started
            </Heading>
          </Column>
        )}
        {date &&
          store &&
          switchButtonValue === SwitchButtonValue.ViewZoneMap &&
          (isGetFloorplanLoading ? (
            <div className="loader-container">
              <Loader type="circular" size="large" />
            </div>
          ) : (
            fpData.length > 0 &&
            isGetZoneDataSuccess && (
              <>
                {draftChanges && (
                  <Toggle checked={showDraft} onChange={setShowDraft}>
                    View draft zone map
                  </Toggle>
                )}
                <Row width="100%" widths={['100%']} height="100%">
                  <ViewZoneComponent
                    floorplanData={fpData}
                    enableClick
                    fixtureIdToZoneDataMap={fixtureIdToZoneDataMapForView}
                    zoneNameKeyMap={zoneNameKeyMapForView}
                  />
                </Row>
              </>
            )
          ))}
        {(switchButtonValue === SwitchButtonValue.CreateZoneMap ||
          switchButtonValue === SwitchButtonValue.CopyToNewMap) &&
          date &&
          store && (
            <Row height="100%" maxHeight="100%" overflowY="hidden" widths={['15%', '85%']}>
              <Column spacingInset="400" spacing="400" minWidth={335} height="100%" overflowY="scroll">
                <Heading level={4}>Create new zone map</Heading>
                {switchButtonValue === SwitchButtonValue.CopyToNewMap && (
                  <CustomDatePicker
                    value={copyToNewDate}
                    onChange={setCopyToNewDate}
                    label="New Live Date"
                    error={!copyToNewDate}
                  />
                )}
                <Select
                  label="Product group"
                  value={selectedProductGroup}
                  onChange={handleProductGroupChange}
                  searchQuery={productGroupFilter}
                  onSearch={setProductGroupFilter}
                  placeholder="Choose a product group..."
                  width={300}
                >
                  {filteredProductGroupOptions.map((option: ProductGroupOption) => (
                    <SelectOption label={option.label} value={option.value} key={option.value} />
                  ))}
                  {!filteredProductGroupOptions.length ? (
                    <Column alignmentVertical="center" spacing="300" spacingInset="600">
                      <Text alignment="center">No results</Text>
                    </Column>
                  ) : null}
                </Select>
                <Select
                  label="Category"
                  value={selectedCategory}
                  onChange={categoryOnChange}
                  searchQuery={categoryFilter}
                  onSearch={setCategoryFilter}
                  placeholder="Choose a category..."
                  width={300}
                >
                  {filteredCategoryOptions?.map((option) => (
                    <SelectOption label={option.categoryName} value={option.categoryId} key={option.categoryId} />
                  ))}
                  {!filteredCategoryOptions || filteredCategoryOptions.length === 0 ? (
                    <Column alignmentVertical="center" spacing="300" spacingInset="600">
                      <Text alignment="center">No results</Text>
                    </Column>
                  ) : null}
                </Select>
                {Array.from(zoneCreationProperties.entries()).map(
                  (zoneCreationEntry: [number, ZoneCreationProperties], index: number) => (
                    <Column spacing="300">
                      <Divider />
                      <ZoneCreationComponent
                        zoneIdx={zoneCreationEntry[0]}
                        fixtures={agileFixtures}
                        selectedFixtures={selectedFixtures}
                        zoneCreationProps={zoneCreationEntry[1]}
                        updateSubcategories={updateSubcategories}
                        updateFixtureIds={updateFixtureIds}
                        updateFacings={updateFacings}
                        removeZone={removeZone}
                        productTaxonomy={productTaxonomy}
                        isActive={activeZoneIdx === zoneCreationEntry[0]}
                        onActivate={() => setActiveZoneIdx(zoneCreationEntry[0])}
                      />
                      {index === zoneCreationProperties.size - 1 && (
                        <Row width={300} widths={[300]}>
                          <Button onClick={() => addAnotherZone(zoneCreationEntry[0] + 1)}>Add another zone</Button>
                        </Row>
                      )}
                    </Column>
                  ),
                )}
                {draftChanges && (
                  <Row width={300} widths={[300]}>
                    <Button onClick={handleDraftZoneCreationSubmit}>Save as Draft zone map</Button>
                  </Row>
                )}
                <Modal
                  title="Zone Creation Validation Error"
                  open={zoneCreationValidationErrors.length > 0}
                  onClose={() => setZoneCreationValidationErrors([])}
                >
                  {zoneCreationValidationErrors.map((error) => (
                    <Text>{error}</Text>
                  ))}
                </Modal>
              </Column>
              <Column height="100%">
                {isGetFloorplanLoading ? (
                  <div className="loader-container">
                    <Loader type="circular" size="large" />
                  </div>
                ) : (
                  fpData.length > 0 && (
                    <ViewZoneComponent
                      floorplanData={fpData}
                      enableClick={false}
                      fixtureIdToZoneDataMap={fixtureIdToEditingZoneDataMap}
                      zoneNameKeyMap={zoneNameKeyMapForEditingZone}
                      selectionEnabled={isSelectionEnabled}
                      activeZoneIdx={activeZoneIdx}
                      tempSelection={fixtureSelection.tempSelection}
                      zones={zoneCreationProperties}
                      onFixtureSelect={handleFixtureClickWithFeedback}
                      onRectangleSelect={handleRectangleSelectionWithFeedback}
                      onActiveZoneChange={setActiveZoneIdx}
                      canUndo={fixtureSelection.canUndo}
                      onUndo={fixtureSelection.undoLastSelection}
                      onCommitSelection={handleCommitSelectionWithFeedback}
                      onClearSelection={fixtureSelection.clearTempSelection}
                    />
                  )
                )}
                <ZoneViewerModalComponent
                  openConfirmationModal={openConfirmationModal}
                  setOpenConfirmationModal={setOpenConfirmationModal}
                  publishZoneErrorMsg={publishZoneErrorMsg}
                  setPublishZoneErrorMsg={setPublishZoneErrorMsg}
                  publishZoneSuccessful={publishZoneSuccessful}
                  setPublishZoneSuccessful={setPublishZoneSuccessful}
                  onPublishButton={onPublishButton}
                  store={store}
                  timeStamp={timeStamp}
                  copyToNewTimestamp={copyToNewTimestamp}
                  zonesToUpdate={zoneCreationProperties}
                  isLoading={isLoadingPublishZone}
                  onClosingSuccessfulModal={onClosingSuccessfulModal}
                  switchButtonValue={switchButtonValue}
                  isDraft={saveAsDraft}
                />
              </Column>
            </Row>
          )}
      </Column>
      {storeOrModeChangeConfirmationModal.open && store && date && (
        <StoreOrModeChangeConfirmationModal
          newStore={newStore}
          switchButtonValue={switchButtonValue}
          setStore={setStore}
          setNewStore={setNewStore}
          setStoreOrModeChangeConfirmationModal={setStoreOrModeChangeConfirmationModal}
          storeChangeConfirmationModal={storeOrModeChangeConfirmationModal}
          setZoneCreationProperties={setZoneCreationProperties}
          initialZoneCreationPropertiesMap={initialZoneCreationPropertiesMap}
          onConfirmSwitchButton={onConfirmSwitchButton}
        />
      )}
    </>
  );
};

export default ZoneViewer;
